import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Loading, PageHeader, Segmented } from '../ui/index.js';
import { fetchJson } from '../api.js';

/**
 * S11 — Audit (docs/09 §6): who did what, when. Two streams: admin actions
 * (user management) and GTIN decisions (approve/reject/supersede/reopen).
 */

interface ActionRow {
  readonly at: string;
  readonly action: string;
  readonly actor: string;
  readonly entity: string;
  readonly ip: string | null;
  readonly summary: string | null;
}

interface DecisionRow {
  readonly at: string;
  readonly action: string;
  readonly actor: string;
  readonly sku: string | null;
  readonly productName: string | null;
  readonly gtin: string | null;
  readonly detail: string | null;
}

type Stream = 'actions' | 'decisions';

const ACTION_LABEL: Record<string, string> = {
  'user.create': 'compte créé',
  'user.update': 'compte modifié',
  'user.password': 'mot de passe défini',
  'user.revoke': 'sessions révoquées',
  approve: 'GTIN approuvé',
  reject: 'GTIN rejeté',
  supersede: 'GTIN transféré',
  reopen: 'rejet annulé',
};

function streamLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

function streamTone(action: string): 'good' | 'signal' | 'muted' | 'alarm' {
  if (action === 'approve' || action === 'user.create') return 'good';
  if (action === 'reject' || action === 'user.revoke') return 'alarm';
  if (action === 'supersede' || action === 'user.password' || action === 'user.update') return 'signal';
  return 'muted';
}

export function AuditPage(): React.ReactElement {
  const [stream, setStream] = useState<Stream>('decisions');
  const auditQuery = useQuery<readonly DecisionRow[] | readonly ActionRow[]>({
    queryKey: ['audit', stream],
    queryFn: () =>
      stream === 'decisions'
        ? fetchJson<readonly DecisionRow[]>('/api/v1/audit?stream=decisions')
        : fetchJson<readonly ActionRow[]>('/api/v1/audit'),
  });

  const rows = auditQuery.data ?? [];

  return (
    <div>
      <PageHeader
        index="S11"
        title="Audit"
        purpose="Qui a fait quoi, quand : décisions GTIN (approuvé, rejeté, transféré) et actions d'administration sur les comptes. Lecture seule — c'est la mémoire de la console."
        meta={[{ label: 'Flux', value: stream === 'decisions' ? 'décisions GTIN' : 'actions admin' }, { label: 'Lignes', value: String(rows.length) }]}
        actions={
          <Segmented<Stream>
            ariaLabel="Flux d'audit"
            value={stream}
            onChange={setStream}
            options={[
              { value: 'decisions', label: 'Décisions GTIN' },
              { value: 'actions', label: 'Actions admin' },
            ]}
          />
        }
      />

      {auditQuery.isError && (
        <Alert tone="alarm" title="Journal indisponible" className="mb-4">
          {String(auditQuery.error.message)}
        </Alert>
      )}
      {auditQuery.isPending && <Loading label="Lecture du journal d'audit…" />}
      {auditQuery.data !== undefined && rows.length === 0 && (
        <Alert tone="info" title="Journal vide">
          {stream === 'decisions'
            ? 'Aucune décision GTIN enregistrée encore — approuvez ou rejetez depuis le Workbench.'
            : 'Aucune action administrateur enregistrée encore (création/modification de comptes, révocations).'}
        </Alert>
      )}

      <div className="space-y-1.5">
        {stream === 'decisions'
          ? (rows as readonly DecisionRow[]).map((row, index) => (
              <div
                key={`${row.at}-${index}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 text-xs"
              >
                <Badge tone={streamTone(row.action)}>{streamLabel(row.action)}</Badge>
                {row.gtin !== null && <span className="font-mono font-bold text-ink">{row.gtin}</span>}
                <span className="min-w-0 flex-1 truncate text-ink-soft">
                  {row.productName !== null ? row.productName : `produit #${row.sku ?? '?'}`}
                  {row.sku !== null && <span className="ml-1.5 font-mono text-ink-faint">{row.sku}</span>}
                </span>
                <span className="font-mono text-ink-faint">{row.actor}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-ghost">
                  {new Date(row.at).toLocaleString('fr-FR')}
                </span>
              </div>
            ))
          : (rows as readonly ActionRow[]).map((row, index) => (
              <div
                key={`${row.at}-${index}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 text-xs"
              >
                <Badge tone={streamTone(row.action)}>{streamLabel(row.action)}</Badge>
                <span className="min-w-0 flex-1 truncate text-ink-soft">
                  {row.entity}
                  {row.summary !== null && <span className="ml-1.5 text-ink-faint">· {row.summary}</span>}
                </span>
                <span className="font-mono text-ink-faint">{row.actor}</span>
                {row.ip !== null && <span className="font-mono text-[10px] text-ink-ghost">{row.ip}</span>}
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-ghost">
                  {new Date(row.at).toLocaleString('fr-FR')}
                </span>
              </div>
            ))}
      </div>
    </div>
  );
}
